import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Chip,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  Tooltip,
  Card,
  CardContent,
  ListItemButton,
  ListItemSecondaryAction,
  Avatar,
  Tabs,
  Tab
} from '@mui/material';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

// Firebase imports
// import { collection, getDocs, query, where, db } from '../firebase';

// Define interfaces for Firebase data
interface FirebaseDocData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  gstin?: string;
  userId?: string;
  createdAt?: string;
  description?: string;
  rate?: number;
  sellingPrice?: number;
  price?: number;
  hsnCode?: string;
  hsn?: string;
  gstRate?: number;
  taxRate?: number;
  category?: string;
  unit?: string;
  stockQuantity?: number;
  stock?: number;
  quantity?: number;
  productName?: string;
  details?: string;
  [key: string]: any;
}

// Interface for firebase query result
interface FirebaseQueryResult {
  docs: Array<{
    id: string;
    data: () => FirebaseDocData;
  }>;
  size: number;
  empty?: boolean;
  forEach: (callback: (doc: { id: string; data: () => FirebaseDocData }) => void) => void;
}

// Define interfaces
interface InvoiceItem {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
  taxAmount?: number;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;
  userId: string;
  createdAt: string;
}

interface InventoryItem {
  id: string;
  name: string;
  description: string;
  price: number;
  hsnCode?: string;
  gstRate?: number;
  category?: string;
  unit?: string;
  stock?: number;
}

interface ItemsSelectionStepProps {
  invoiceData: InvoiceData;
  updateInvoiceData: (data: Partial<InvoiceData>) => void;
  inventory?: InventoryItem[];
  userId?: string;
}

const ItemsSelectionStep: React.FC<ItemsSelectionStepProps> = ({
  invoiceData,
  updateInvoiceData,
  inventory = [],
  userId
}) => {
  // State for inventory items
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>(inventory);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>(inventory);
  const [selectedItems, setSelectedItems] = useState<InventoryItem[]>([]);
  const [items, setItems] = useState<InvoiceItem[]>(invoiceData.items || []);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Summary state
  const [subtotal, setSubtotal] = useState(invoiceData.subtotal || 0);
  const [taxAmount, setTaxAmount] = useState(invoiceData.tax || 0);
  const [discountAmount, setDiscountAmount] = useState(invoiceData.discount || 0);
  const [total, setTotal] = useState(invoiceData.amount || 0);

  // Load inventory items on mount
  useEffect(() => {
    if (inventory && inventory.length > 0) {
      setInventoryItems(inventory);
      setFilteredItems(inventory);

      // Extract categories from inventory
      const categorySet = new Set<string>();
      inventory.forEach(item => {
        if (item.category) {
          categorySet.add(item.category);
        }
      });

      setCategories(Array.from(categorySet));
    } else {
      // Fetch inventory items if not provided
      fetchInventoryItems();
    }

    // Initialize selected items from invoiceData
    if (invoiceData.items && invoiceData.items.length > 0) {
      const selectedFromInvoice = invoiceData.items.map(item => {
        const inventoryItem = inventory.find(invItem => invItem.id === item.inventoryItemId);
        if (inventoryItem) {
          return inventoryItem;
        }
        return null;
      }).filter(Boolean) as InventoryItem[];

      setSelectedItems(selectedFromInvoice);
      setItems(invoiceData.items);
    }
  }, [inventory, invoiceData.items]);

  // Fetch inventory items from database
  const fetchInventoryItems = async () => {
    setLoading(true);
    try {
      const indexedDBService = (await import('../../utils/indexedDBService')).default;

      // Try fetching from 'inventory' collection first
      const inventoryItems = await indexedDBService.getAllItems<any>('inventory');

      // Filter by userId if available
      let userInventory = userId ? inventoryItems.filter((i: any) => i.userId === userId) : inventoryItems;

      if (userInventory.length > 0) {
        const items: InventoryItem[] = userInventory.map(data => {
          return {
            id: data.id,
            name: data.name || 'Unnamed Item',
            description: data.description || '',
            price: data.sellingPrice || data.rate || data.price || 0,
            hsnCode: data.hsnCode || '',
            gstRate: data.gstRate || 18,
            category: data.category || 'Uncategorized',
            unit: data.unit || 'Nos',
            stock: data.stockQuantity || data.stock || 0
          };
        });

        const categorySet = new Set<string>();
        items.forEach(item => {
          if (item.category) {
            categorySet.add(item.category);
          }
        });

        setInventoryItems(items);
        setFilteredItems(items);
        setCategories(Array.from(categorySet));
        return;
      }

      // If no items found, try 'inventoryItems' collection
      // Mocking fallback behavior with other stores if they exist in IDB
      // The original code tried 'inventoryItems' and 'products'.

      // We'll stick to 'inventory' for now as that's what we migrated to. 
      // If we need to support legacy stores in IDB, we can add them here.

      setInventoryItems([]);
      setFilteredItems([]);
      setCategories([]);

    } catch (error) {
      console.error('Error fetching inventory items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.trim() === '') {
      // Apply only category filter when search is empty
      filterItemsByCategory(activeCategory);
    } else {
      // Filter by search query and category
      const filtered = inventoryItems.filter(item => {
        const nameMatch = (item.name || '').toLowerCase().includes(query);
        const descMatch = (item.description || '').toLowerCase().includes(query);
        const hsnMatch = (item.hsnCode || '').toLowerCase().includes(query);
        const searchMatch = nameMatch || descMatch || hsnMatch;

        // Apply category filter if active
        if (activeCategory !== 'all') {
          return searchMatch && item.category === activeCategory;
        }

        return searchMatch;
      });

      setFilteredItems(filtered);
    }
  };

  // Filter items by category
  const filterItemsByCategory = (category: string) => {
    setActiveCategory(category);

    if (category === 'all') {
      // If search query exists, apply search filter
      if (searchQuery) {
        const filtered = inventoryItems.filter(item => {
          const nameMatch = (item.name || '').toLowerCase().includes(searchQuery);
          const descMatch = (item.description || '').toLowerCase().includes(searchQuery);
          const hsnMatch = (item.hsnCode || '').toLowerCase().includes(searchQuery);

          return nameMatch || descMatch || hsnMatch;
        });

        setFilteredItems(filtered);
      } else {
        // Show all items
        setFilteredItems(inventoryItems);
      }
    } else {
      // Filter by category
      const filtered = inventoryItems.filter(item => {
        const categoryMatch = item.category === category;

        // Also apply search filter if exists
        if (searchQuery) {
          const nameMatch = (item.name || '').toLowerCase().includes(searchQuery);
          const descMatch = (item.description || '').toLowerCase().includes(searchQuery);
          const hsnMatch = (item.hsnCode || '').toLowerCase().includes(searchQuery);

          return categoryMatch && (nameMatch || descMatch || hsnMatch);
        }

        return categoryMatch;
      });

      setFilteredItems(filtered);
    }
  };

  // Check if an item is selected
  const isItemSelected = (itemId: string) => {
    return selectedItems.some(item => item.id === itemId);
  };

  // Handle item selection
  const handleItemSelect = (item: InventoryItem) => {
    // Check if item is already selected
    if (isItemSelected(item.id)) {
      // Remove from selection
      const newSelectedItems = selectedItems.filter(selectedItem => selectedItem.id !== item.id);
      setSelectedItems(newSelectedItems);

      // Remove from invoice items
      const newItems = items.filter(invoiceItem => invoiceItem.inventoryItemId !== item.id);
      setItems(newItems);

      // Update invoice data
      updateInvoiceData({ items: newItems });
      updateTotals(newItems);
    } else {
      // Add to selection
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);

      // Create new invoice item
      const newInvoiceItem: InvoiceItem = {
        id: Date.now(), // Generate temporary ID
        name: item.name,
        description: item.description || '',
        quantity: 1,
        rate: item.price,
        amount: item.price, // Quantity * Rate
        inventoryItemId: item.id,
        hsnCode: item.hsnCode,
        gstRate: item.gstRate,
        taxAmount: 0 // Will be calculated in updateTotals
      };

      const newItems = [...items, newInvoiceItem];
      setItems(newItems);

      // Update invoice data
      updateInvoiceData({ items: newItems });
      updateTotals(newItems);
    }
  };

  // Update item quantity
  const handleQuantityChange = (itemId: number, quantity: number) => {
    if (quantity <= 0) return;

    const newItems = items.map(item => {
      if (item.id === itemId) {
        const newAmount = quantity * item.rate;
        return {
          ...item,
          quantity,
          amount: newAmount
        };
      }
      return item;
    });

    setItems(newItems);
    updateInvoiceData({ items: newItems });
    updateTotals(newItems);
  };

  // Update item rate
  const handleRateChange = (itemId: number, rate: number) => {
    if (rate < 0) return;

    const newItems = items.map(item => {
      if (item.id === itemId) {
        const newAmount = item.quantity * rate;
        return {
          ...item,
          rate,
          amount: newAmount
        };
      }
      return item;
    });

    setItems(newItems);
    updateInvoiceData({ items: newItems });
    updateTotals(newItems);
  };

  // Remove item from invoice
  const handleRemoveItem = (itemId: number) => {
    // Find the inventory item id to remove from selected items too
    const invoiceItem = items.find(item => item.id === itemId);
    if (invoiceItem && invoiceItem.inventoryItemId) {
      setSelectedItems(prev => prev.filter(item => item.id !== invoiceItem.inventoryItemId));
    }

    const newItems = items.filter(item => item.id !== itemId);
    setItems(newItems);
    updateInvoiceData({ items: newItems });
    updateTotals(newItems);
  };

  // Update totals
  const updateTotals = (newItems: InvoiceItem[]) => {
    // Calculate subtotal
    const calculatedSubtotal = newItems.reduce(
      (total, item) => total + item.amount,
      0
    );

    // Calculate discount amount
    let calculatedDiscountAmount = 0;
    if (invoiceData.discountType === 'percentage') {
      calculatedDiscountAmount = calculatedSubtotal * (invoiceData.discountValue / 100);
    } else {
      calculatedDiscountAmount = invoiceData.discountValue;
    }

    // Ensure discount doesn't exceed subtotal
    calculatedDiscountAmount = Math.min(calculatedDiscountAmount, calculatedSubtotal);

    // Check if client has GSTIN
    const hasGstin = invoiceData.clientDetails?.gstin && invoiceData.clientDetails.gstin.trim() !== '';

    // Calculate taxable amount and tax - only if GSTIN is provided
    const taxableAmount = calculatedSubtotal - calculatedDiscountAmount;
    const calculatedTaxAmount = (invoiceData.taxable && hasGstin) ? taxableAmount * (invoiceData.taxRate / 100) : 0;

    // Calculate total
    const calculatedTotal = taxableAmount + calculatedTaxAmount;

    // Update state
    setSubtotal(calculatedSubtotal);
    setDiscountAmount(calculatedDiscountAmount);
    setTaxAmount(calculatedTaxAmount);
    setTotal(calculatedTotal);

    // Update invoice data
    updateInvoiceData({
      subtotal: calculatedSubtotal,
      discount: calculatedDiscountAmount,
      tax: calculatedTaxAmount,
      amount: calculatedTotal
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Group items by category for display
  const getItemsByCategory = () => {
    const itemsByCategory: Record<string, InventoryItem[]> = {};

    filteredItems.forEach(item => {
      const category = item.category || 'Uncategorized';
      if (!itemsByCategory[category]) {
        itemsByCategory[category] = [];
      }
      itemsByCategory[category].push(item);
    });

    return itemsByCategory;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Item Selection Section */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Select Items
            </Typography>

            {/* Search and Filter */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search items by name, description or HSN code"
                value={searchQuery}
                onChange={handleSearchChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowFilterOptions(!showFilterOptions)}
                        color={showFilterOptions ? "primary" : "default"}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Filter options */}
            <Collapse in={showFilterOptions}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Filter Options
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowFilterOptions(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <FormControl component="fieldset" size="small">
                  <FormLabel component="legend">Categories</FormLabel>
                  <RadioGroup
                    row
                    value={activeCategory}
                    onChange={(e) => filterItemsByCategory(e.target.value)}
                  >
                    <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                    {categories.map(category => (
                      <FormControlLabel
                        key={category}
                        value={category}
                        control={<Radio size="small" />}
                        label={category}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Paper>
            </Collapse>

            {/* Category Tabs */}
            <Tabs
              value={activeCategory === 'all' ? 0 : categories.indexOf(activeCategory) + 1}
              onChange={(e, newValue) => {
                if (newValue === 0) {
                  filterItemsByCategory('all');
                } else {
                  filterItemsByCategory(categories[newValue - 1]);
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label="All Items"
                icon={<ShoppingCartIcon fontSize="small" />}
                iconPosition="start"
              />
              {categories.map((category, index) => (
                <Tab
                  key={category}
                  label={category}
                  icon={<CategoryIcon fontSize="small" />}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            {/* Item Selection List */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {filteredItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No items match your search criteria
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {Object.entries(getItemsByCategory()).map(([category, categoryItems]) => (
                    <Box key={category} sx={{ mb: 3 }}>
                      {/* Only show category heading if not filtering by category or showing all */}
                      {(activeCategory === 'all' || categories.length > 1) && (
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            backgroundColor: 'action.hover',
                            padding: '4px 8px',
                            borderRadius: 1
                          }}
                        >
                          <CategoryIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {category}
                        </Typography>
                      )}

                      <List disablePadding>
                        {categoryItems.map((item) => (
                          <ListItem
                            key={item.id}
                            disablePadding
                            sx={{ mb: 1 }}
                          >
                            <ListItemButton
                              onClick={() => handleItemSelect(item)}
                              dense
                              sx={{
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: isItemSelected(item.id) ? 'primary.main' : 'divider',
                                backgroundColor: isItemSelected(item.id) ? 'primary.light' : 'transparent'
                              }}
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isItemSelected(item.id)}
                                  tabIndex={-1}
                                  disableRipple
                                  color="primary"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2">
                                    {item.name}
                                    {item.gstRate && (
                                      <Chip
                                        size="small"
                                        label={`GST ${item.gstRate}%`}
                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                        color="info"
                                      />
                                    )}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    {item.description && (
                                      <Typography variant="body2" color="text.secondary">
                                        {item.description.substring(0, 60)}{item.description.length > 60 ? '...' : ''}
                                      </Typography>
                                    )}
                                    <Typography variant="body2" color="primary" fontWeight="medium">
                                      {formatCurrency(item.price)} {item.unit && `per ${item.unit}`}
                                    </Typography>
                                  </Box>
                                }
                              />
                              {isItemSelected(item.id) && (
                                <CheckCircleIcon color="primary" sx={{ ml: 1 }} />
                              )}
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Selection Summary */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedItems.length === 0
                  ? 'No items selected'
                  : `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Selected Items Section */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Selected Items
            </Typography>

            {items.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No items added to this invoice yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select items from the list on the left
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell component="th" scope="row">
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {item.name}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" color="text.secondary">
                                {item.description.substring(0, 30)}{item.description.length > 30 ? '...' : ''}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(item.id, Number(e.target.value))}
                            InputProps={{
                              inputProps: {
                                min: 1,
                                style: { textAlign: 'right', padding: '4px 8px' }
                              }
                            }}
                            sx={{ width: '60px' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={item.rate}
                            onChange={(e) => handleRateChange(item.id, Number(e.target.value))}
                            InputProps={{
                              inputProps: {
                                min: 0,
                                style: { textAlign: 'right', padding: '4px 8px' }
                              }
                            }}
                            sx={{ width: '80px' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Invoice Summary */}
            {items.length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(subtotal)}
                    </Typography>
                  </Grid>

                  {discountAmount > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2">Discount:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="error">
                          - {formatCurrency(discountAmount)}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {taxAmount > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2">Tax ({invoiceData.taxRate}%):</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          {formatCurrency(taxAmount)}
                        </Typography>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Total:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {formatCurrency(total)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ItemsSelectionStep; 